// ---------------------------------------------------------------------------
// Section 9: Events — event bindings to ActionFlows
// ---------------------------------------------------------------------------

import { useCallback, useMemo } from 'react'
import { useEditorStore } from '@/features/page-editor/store/editor-store'
import { useFlowEditorStore } from '@/features/flow-editor/store/flow-editor-store'
import {
  flowNamesMatch,
  isReservedFlowName,
  resolveFlowOwner,
  type OwnedBlock,
} from '@/features/flow-editor/services/flow-ownership'
import { useStudioStore } from '@/store/studio-store'
import { mcpBus } from '@/mcp/studio-mcp-bus'
import { toast } from '@/store/toast-store'
import { InspectorSection } from '@/components/inspector'
import { EventBindingRow } from '../event-binding-row'
import type { CogoElement, CogoEventBinding } from '@/features/page-editor/store/types'

/** Element types that support specific events */
const ELEMENT_EVENTS: Record<string, string[]> = {
  button: ['tap', 'hover'],
  container: ['tap', 'hover'],
  card: ['tap', 'hover'],
  section: ['tap'],
  image: ['tap'],
  icon: ['tap'],
  text: ['tap'],
  heading: ['tap'],
  input: ['change', 'focus', 'blur'],
  textarea: ['change', 'focus', 'blur'],
  select: ['change', 'focus', 'blur'],
  checkbox: ['change'],
  form: ['submit'],
  badge: ['tap'],
  link: ['tap'],
}

/** Default events for types not explicitly listed */
const DEFAULT_EVENTS = ['tap']

/**
 * COR-8: whether a session FlowMeta name and a candidate (persisted) name
 * refer to the SAME flow. flowNamesMatch alone bare-matches a namespaced
 * session name onto ANY bare persisted name — but the ownership doc
 * (isFlowNameTaken) treats e.g. declared 'other.addToCart' vs 'addToCart'
 * as two DIFFERENT flows. When BOTH names resolve to declarations, trust
 * the resolution: a different owner/key means different flows (keep both
 * picker entries; never bind the wrong meta). Unresolvable names
 * (never-persisted drafts, blockless sessions) keep the plain
 * flowNamesMatch dedupe — a namespaced draft still dedupes against the bare
 * declaration its saves will merge into.
 */
function isSameFlowReference(
  blocks: Record<string, OwnedBlock>,
  sessionName: string,
  candidateName: string,
): boolean {
  if (!flowNamesMatch(sessionName, candidateName)) return false
  if (sessionName.toLowerCase() === candidateName.toLowerCase()) return true
  const sessionOwner = resolveFlowOwner(blocks, sessionName)
  const candidateOwner = resolveFlowOwner(blocks, candidateName)
  if (!sessionOwner || !candidateOwner) return true
  return (
    sessionOwner.blockName === candidateOwner.blockName &&
    sessionOwner.flowKey === candidateOwner.flowKey
  )
}

/** Mirrors flow-slice's isStudioFlowToolRegistered (F4 manifest check):
 *  component tests / embeds render this section without
 *  registerStudioTools() — the bus op would silently fail, so bind/unbind
 *  fall back to the direct element-level write (degraded: the renderer
 *  keeps working; block leg + undo coverage unavailable — the pre-F8 shape). */
function isEventBindingToolRegistered(tool: 'addEventBinding' | 'removeEventBinding'): boolean {
  return mcpBus.getManifest().some((t) => t.name === `cogo.studio.${tool}`)
}

/** Studio project blocks in the shape the ownership helpers read. */
function studioOwnedBlocks(): Record<string, OwnedBlock> {
  return (useStudioStore.getState().currentProject?.blocks ?? {}) as Record<string, OwnedBlock>
}

export function EventsSection({
  elementId,
  blockName,
  element,
}: {
  elementId: string
  blockName: string
  element: CogoElement
}) {
  const setElementEvent = useEditorStore((s) => s.setElementEvent)
  const removeElementEvent = useEditorStore((s) => s.removeElementEvent)
  // Persisted (load-time) flow names — cover projects where the Behavior
  // surface never mounted this session (useHydrateBehaviorFlows is the ONLY
  // flowEditorStore hydration, so flowEditorStore may still be empty).
  const persistedFlows = useEditorStore((s) => s.flows)
  // Session truth for flow metadata (F8): flows created this session in the
  // Behavior surface — or right here via the picker — live ONLY in
  // flowEditorStore until their first persisted save.
  const flowMetas = useFlowEditorStore((s) => s.flows)

  // Studio project blocks — ownership resolution input for the COR-8
  // distinct-flow check below (declarations live on the blocks).
  const studioBlocks = useStudioStore((s) => s.currentProject?.blocks)

  const availableFlows = useMemo(() => {
    // Union of session FlowMeta names and persisted names, deduped with the
    // ownership full-then-bare rule (flowNamesMatch): a namespaced session
    // draft ("store-nav.addToCart") and its persisted bare declaration
    // ("addToCart") are the SAME flow — listed once, session name preferred.
    // COR-8: a bare-name match that ownership resolution proves points at a
    // DIFFERENT declaration (e.g. persisted 'addToCart' on block A vs
    // session 'other.addToCart' declared on block B) is NOT deduped — both
    // flows stay bindable.
    const blocks = (studioBlocks ?? {}) as Record<string, OwnedBlock>
    const names = flowMetas.map((meta) => meta.name)
    for (const persisted of Object.keys(persistedFlows)) {
      if (!names.some((name) => isSameFlowReference(blocks, name, persisted))) {
        names.push(persisted)
      }
    }
    return names.sort((a, b) => a.localeCompare(b))
  }, [flowMetas, persistedFlows, studioBlocks])
  const supportedEvents = ELEMENT_EVENTS[element.type] ?? DEFAULT_EVENTS
  const existingBindings = element.events ?? {}
  const hasBindings = Object.keys(existingBindings).length > 0

  const handleBind = useCallback(
    (eventType: string, ref: CogoEventBinding) => {
      const flowName = ref.flow ?? ref.action
      const store = useFlowEditorStore.getState()
      // Exact id/name match first; a bare-name match only counts when
      // ownership resolution does not prove the two names are DIFFERENT
      // flows (COR-8 — never bind the wrong meta on a name collision).
      const blocks = studioOwnedBlocks()
      const meta = flowName
        ? (store.flows.find(
            (f) => f.id === flowName || f.name.toLowerCase() === flowName.toLowerCase(),
          ) ?? store.flows.find((f) => isSameFlowReference(blocks, f.name, flowName)))
        : undefined
      if (meta && isEventBindingToolRegistered('addEventBinding')) {
        // RUX-01/COR-4: ONE bus operation writes BOTH representations —
        // element-level `element.events` (renderer-read, ui-field persisted)
        // AND block-level `events.bindings` — with ONE inverse, so Cmd+Z
        // reverts both legs and re-queues both field saves. The pre-fix
        // dual-write recorded only the block leg; undo left the element
        // binding alive and persisted diverged state.
        store.bindFlowToEvent(meta.id, blockName, elementId, eventType, ref)
        return
      }
      // Degraded fallback — DIRECT element-level write only (the pre-F8
      // shape: the renderer keeps working; the block leg and undo coverage
      // do not apply):
      //  - RUX-02 (accepted residual): no session FlowMeta — flowEditorStore
      //    is unhydrated (Behavior surface never mounted) or the picked flow
      //    is persisted-only. The block-level gap heals on the next Behavior
      //    hydration; no invasive hydration is attempted here.
      //  - no registered studio tools (component tests / embeds): the bus
      //    op would silently no-op.
      if (!meta) {
        console.debug(
          `[events-section] No session FlowMeta for "${flowName}" (flowEditorStore unhydrated or persisted-only flow) — binding element-level only`,
        )
      }
      setElementEvent(blockName, elementId, eventType, ref)
    },
    [setElementEvent, blockName, elementId],
  )

  const handleUnbind = useCallback(
    (eventType: string) => {
      if (isEventBindingToolRegistered('removeEventBinding')) {
        // RUX-01/COR-4: atomic dual-removal — one inverse restores both the
        // element-level and the block-level binding (and re-queues both
        // field saves). The pre-fix removeElementEvent call was permanent.
        useFlowEditorStore
          .getState()
          .unbindFlowFromEvent(blockName, elementId, eventType, { removeElementEvent: true })
        return
      }
      // No-tools fallback: direct element-level removal only (degraded).
      removeElementEvent(blockName, elementId, eventType)
    },
    [removeElementEvent, blockName, elementId],
  )

  const handleCreateFlow = useCallback((flowName: string): boolean => {
    if (isReservedFlowName(flowName)) {
      // Reserved key (CS-1) — the store guard rejects it; name the reason.
      toast.error(`"${flowName}" is a reserved name`)
      return false
    }
    // Canonical creation path — exactly what the Behavior surface's
    // CreateFlowForm and FlowStepPanel do. The flow appears in the Behavior
    // list immediately and persists through the single flow auto-save path
    // once steps are added. Side effect: the Behavior surface selection
    // jumps to the new flow (createFlow selects it).
    const created = useFlowEditorStore.getState().createFlow(flowName, 'event')
    if (created === null) {
      toast.error(`A flow named "${flowName}" already exists`)
      return false
    }
    return true
  }, [])

  return (
    <InspectorSection title="Events" defaultOpen={hasBindings}>
      <div className="space-y-0.5">
        {supportedEvents.map((eventType) => {
          const rawBinding = existingBindings[eventType]
          const binding = typeof rawBinding === 'string'
            ? { flow: rawBinding }
            : rawBinding

          return (
            <EventBindingRow
              key={eventType}
              eventType={eventType}
              binding={binding}
              availableFlows={availableFlows}
              onBind={(ref) => handleBind(eventType, ref)}
              onUnbind={() => handleUnbind(eventType)}
              onCreateFlow={handleCreateFlow}
            />
          )
        })}
      </div>
    </InspectorSection>
  )
}
